import type { CompositionV3BreakdownOptions } from '@revelio/data-access';
import { EntKind } from '@revelio/data-access';

import type { GeographyMode } from '../../../../../shared/user-preferences/types';
import {
  getEntKindForSkillLevelId,
  skillEntKindFromTier,
} from '../role-skill-ent-kind';
import {
  getTreemapDrilldownIsLeafLevel,
  getTreemapDrilldownLevelOrder,
  type TreemapDrilldownSegment,
} from '../treemapDrilldownLevels';
import type { CompositionV3BreakdownLevelOverrides } from './getCompositionV3BreakdownOptions';

const GEOGRAPHY_LEVEL_ORDER_BY_MODE: Record<GeographyMode, readonly EntKind[]> =
  {
    msa: [EntKind.Region, EntKind.Country, EntKind.Msa, EntKind.City],
    administrative: [
      EntKind.Region,
      EntKind.Country,
      EntKind.State,
      EntKind.City,
    ],
  };

const ROLE_LEVEL_ORDER: readonly EntKind[] = [
  EntKind.RoleLvl_1,
  EntKind.RoleLvl_3,
  EntKind.RoleLvl_5,
  EntKind.RoleLvl_7,
];

const RICS_LEVEL_ORDER: readonly EntKind[] = [
  EntKind.RicsK10,
  EntKind.RicsK50,
  EntKind.RicsK400,
];

function skillLevelOrder(): readonly EntKind[] {
  return getTreemapDrilldownLevelOrder('skills').map(
    (levelId, index) =>
      getEntKindForSkillLevelId(levelId) ?? skillEntKindFromTier(index + 1)
  );
}

function levelOrderForSegment(
  segment: TreemapDrilldownSegment,
  geographyMode: GeographyMode
): readonly EntKind[] {
  switch (segment) {
    case 'geography':
      return GEOGRAPHY_LEVEL_ORDER_BY_MODE[geographyMode];
    case 'role':
      return ROLE_LEVEL_ORDER;
    case 'rics':
      return RICS_LEVEL_ORDER;
    case 'skills':
      return skillLevelOrder();
  }
}

export type BreakdownLevelField = keyof Pick<
  CompositionV3BreakdownOptions,
  | 'geo_breakdown_level'
  | 'role_breakdown_level'
  | 'rics_breakdown_level'
  | 'skill_breakdown_level'
>;

const BREAKDOWN_LEVEL_FIELD_BY_SEGMENT: Record<
  TreemapDrilldownSegment,
  BreakdownLevelField
> = {
  geography: 'geo_breakdown_level',
  role: 'role_breakdown_level',
  rics: 'rics_breakdown_level',
  skills: 'skill_breakdown_level',
};

export function getBreakdownLevelField(
  segment: TreemapDrilldownSegment
): BreakdownLevelField {
  return BREAKDOWN_LEVEL_FIELD_BY_SEGMENT[segment];
}

export function getBreakdownLevelDepth(
  segment: TreemapDrilldownSegment,
  level: EntKind,
  geographyMode: GeographyMode
): number | null {
  const order = levelOrderForSegment(segment, geographyMode);
  const index = order.indexOf(level);
  return index === -1 ? null : index;
}

export function getBreakdownLevelAtDepth(
  segment: TreemapDrilldownSegment,
  depth: number,
  geographyMode: GeographyMode
): EntKind | null {
  const order = levelOrderForSegment(segment, geographyMode);
  return order[depth] ?? null;
}

export type NestedTreemapViewDerivation = {
  /** Depth of the displayed cells, or `null` when the view renders flat. */
  childDepth: number | null;
  breakdownLevelOverrides: CompositionV3BreakdownLevelOverrides | undefined;
  parentEntKind: EntKind | null;
  parentBreakdownLevelOverrides:
    | CompositionV3BreakdownLevelOverrides
    | undefined;
  displayLevelId: string;
  isDisplayLeaf: boolean;
  parentLevelId: string | null;
};

// Group headers sit at the filters' natural breakdown level; displayed cells
// one deeper (falling back to a flat view when the tree has no deeper level).
export function deriveNestedTreemapView({
  segment,
  naturalLevel,
  geographyMode,
  isRicsCompanyBreakdown,
}: {
  segment: TreemapDrilldownSegment;
  naturalLevel: EntKind;
  geographyMode: GeographyMode;
  isRicsCompanyBreakdown: boolean;
}): NestedTreemapViewDerivation {
  const levelOrder = getTreemapDrilldownLevelOrder(segment, geographyMode);
  const levelField = getBreakdownLevelField(segment);
  const naturalDepth = getBreakdownLevelDepth(
    segment,
    naturalLevel,
    geographyMode
  );

  const { childDepth, breakdownLevelOverrides } = ((): {
    childDepth: number | null;
    breakdownLevelOverrides: CompositionV3BreakdownLevelOverrides | undefined;
  } => {
    if (isRicsCompanyBreakdown || naturalDepth === null) {
      return { childDepth: null, breakdownLevelOverrides: undefined };
    }
    const childLevel = getBreakdownLevelAtDepth(
      segment,
      naturalDepth + 1,
      geographyMode
    );
    if (childLevel === null) {
      return {
        childDepth: naturalDepth === 0 ? null : naturalDepth,
        breakdownLevelOverrides: undefined,
      };
    }
    const overrides: CompositionV3BreakdownLevelOverrides = {};
    overrides[levelField] = childLevel;
    return { childDepth: naturalDepth + 1, breakdownLevelOverrides: overrides };
  })();

  const displayDepth = childDepth ?? naturalDepth ?? 0;
  const displayLevelId = isRicsCompanyBreakdown
    ? 'company'
    : (levelOrder[displayDepth] ?? levelOrder[levelOrder.length - 1] ?? '');
  const isDisplayLeaf =
    isRicsCompanyBreakdown ||
    getTreemapDrilldownIsLeafLevel(segment, displayLevelId);
  const parentLevelId =
    childDepth !== null && childDepth >= 1
      ? (levelOrder[childDepth - 1] ?? null)
      : null;

  const parentEntKind =
    childDepth !== null && childDepth >= 1
      ? getBreakdownLevelAtDepth(segment, childDepth - 1, geographyMode)
      : null;
  const parentBreakdownLevelOverrides = (():
    | CompositionV3BreakdownLevelOverrides
    | undefined => {
    if (parentEntKind === null) return undefined;
    const overrides: CompositionV3BreakdownLevelOverrides = {};
    overrides[levelField] = parentEntKind;
    return overrides;
  })();

  return {
    childDepth,
    breakdownLevelOverrides,
    parentEntKind,
    parentBreakdownLevelOverrides,
    displayLevelId,
    isDisplayLeaf,
    parentLevelId,
  };
}
